import { CommunityClient } from './CommunityClient';
import { AlertPresenter } from '../AlertBinder';
import { HistoryViewModel } from '../support/HistoryViewModel';
import { Logger } from '../support/Logger';
import { MixPanelEventEmitter as MixpanelService } from '../metrics/MixPanelEventEmitter';
import { UserState } from '../user/UserState';
import { ICaremapAccess } from '../caremap/ICareMap';
import { OrganizationsState } from '../organizations/OrganizationsState';

export class UserAcceptInviteAlert {
    static inject = () => [
        AlertPresenter,
        CommunityClient,
        HistoryViewModel,
        MixpanelService,
        UserState,
        Logger,
        OrganizationsState
    ];
    constructor(
        private alert: AlertPresenter,
        private client: CommunityClient,
        private history: HistoryViewModel,
        private tracking: MixpanelService,
        private userState: UserState,
        private logger: Logger,
        private orgsState: OrganizationsState
    ) {}

    show = async ({ token, access, patient }: { token: string; access: ICaremapAccess; patient?: string; }) => {
        let header = 'Accept Co-Owner Invite?';
        let message = 'To accept this invite, please review and agree to the following: 1) I certify that I have permission to manage a care profile on behalf of the Care Recipient. 2) The Care Recipient consents that Innerhive can use the data I enter on their behalf for the purpose of organizing and managing their care.';

        if (access === 'view-only'){
            header = 'Accept Viewer Invite?';
            message = `Become a member of Innerhive with viewer access and stay in the loop with care notifications and important updates.`;
            if (patient){
                message = `Become a member of ${patient}'s innerhive with viewer access and stay in the loop with care notifications and important updates.`;
            }
        }

        const action = await this.alert.showAlertV2({
            header,
            message,
            wider: true,
            options: [{
                id: 'decline',
                label: 'Decline',
                role: 'destructive'
            }, {
                id: 'consent',
                label: 'Accept'
            }]
        });

        if (action === 'consent') {
            const { careMap, careMapCategory } = await this.client.acceptedInvite(token);
            console.log('added caremap to caremap state', careMap.careMapId);

            // The feed is the caregiver's landing place for a caremap they have just joined. An
            // agency user has no community of their own around a client, so it leaves them on a
            // page built for somebody else's relationship to the care recipient. They came here to
            // reach the client's record, so take them to it.
            this.history.push(this.orgsState.isOrgTeamMember()
                ? `/care/${careMap.careMapId}`
                : `/community/${careMap.careMapId}/feed`);

            const trackingKey = access === 'co-owner' ? 'Co-Owner Invite Accepted' : 'Permanent Viewer Invite Accepted';
            this.tracking.track(trackingKey, () => ({ 'Invite Email': this.userState.state.email, 'Care Map Category': careMapCategory }));
        } else {
            try {
                await this.client.declinedInvite(token);
            } catch (e: any) {
                // when accepting your OWN invite, returns a 409 err.status, err.errorCode
                // it's not rejected from an API perspective, should show the user the message
                // this will NOT go to Sentry (showAndLogError doesn't log to Sentry because it's < 500)
                if (e.status === 409 && e.errorCode === 'cannot-accept-own-invite') {
                    throw e;
                } else {
                    // ALL decline errors should be swallowed, but still send to SENTRY
                    if ((e?.status || 500) >= 500) {
                        this.logger.error(e);
                    }
                }
            }

            this.history.push('/');
        }
    };
}
